import {attr, controller, target, targets} from '@github/catalyst'
import {currencyFormatter, getUnitData} from './utils'
import type {UnitData} from './utils'

@controller('copilot-calculator')
export class CopilotCalculatorElement extends HTMLElement {
  static TOTAL_COST_PER_MONTH = 'Total cost per month'
  static AI_CREDITS = 'ai_credits'
  static LEGACY_OVERAGE = 'overage'
  static LEGACY_OVERAGE_REQUESTS = 'overage_requests'
  static AI_CREDIT_COST_IN_CENTS = 1
  static LEGACY_OVERAGE_COST_IN_CENTS = 4

  @targets declare plans: HTMLInputElement[]
  @targets declare planLabels: HTMLElement[]
  @targets declare developerCountInputs: HTMLInputElement[]
  @target declare costPerMonthDisplay: HTMLElement

  get selectedPlan() {
    return this.plans.find(planOption => planOption.checked)
  }

  get plan() {
    return (this.selectedPlan && this.selectedPlan.value) || ''
  }

  get planPrice() {
    if (!this.unit_data) {
      this.unit_data = getUnitData()
      if (!this.unit_data) return 0
    }

    const planData = this.unit_data[this.plan]
    if (!planData?.value) return 0

    return planData.value
  }

  getDeveloperCount(planType: string): number {
    const inputElement = this.developerCountInputs.find(input => input.getAttribute('data-plan-type') === planType)
    if (!inputElement) return 0

    const value = inputElement.value.trim()
    if (!value) return 0

    const count = parseInt(value)
    return !isNaN(count) && count > 0 ? count : 0
  }

  static usageCostInCents(planType: string): number | null {
    if (planType === CopilotCalculatorElement.AI_CREDITS) {
      return CopilotCalculatorElement.AI_CREDIT_COST_IN_CENTS
    }
    if (planType === CopilotCalculatorElement.LEGACY_OVERAGE) {
      return CopilotCalculatorElement.LEGACY_OVERAGE_COST_IN_CENTS
    }
    return null
  }

  @attr costPerMonth = 0
  @attr active = false

  declare unit_data: UnitData

  connectedCallback() {
    // Load unit data as soon as element is connected
    this.unit_data = getUnitData()

    if (this.selectedPlan) {
      this.updateCost()
    }

    // Watch for section activation
    const section = this.closest('.calculator-section')
    if (section) {
      const observer = new MutationObserver(mutations => {
        for (const mutation of mutations) {
          if (mutation.target instanceof HTMLElement && mutation.attributeName === 'class') {
            const isHidden = mutation.target.classList.contains('calculator-section--hidden')
            if (!isHidden) {
              this.active = true
              this.updateCost()
            }
          }
        }
      })
      observer.observe(section, {
        attributes: true,
        attributeFilter: ['class'],
        attributeOldValue: true,
      })
    }
  }

  planUpdated(event: Event) {
    this.setAttribute('data-active', 'true')

    for (const planLabel of this.planLabels) {
      planLabel.classList.remove('gradient-border')
    }
    const plan = event.currentTarget as HTMLInputElement
    const label = plan.closest('label')
    if (label) {
      label.classList.add('gradient-border')
    }

    // Clear all developer count inputs when switching plans
    for (const input of this.developerCountInputs) {
      input.value = ''
    }

    this.updateCost()
  }

  developerCountChanged(event: Event) {
    this.setAttribute('data-active', 'true')
    const input = event.target as HTMLInputElement
    const planType = input.getAttribute('data-plan-type')
    const value = input.value.trim()
    const count = parseInt(value)

    // Clear invalid values and provide feedback
    if (value !== '' && (isNaN(count) || count < 1)) {
      input.value = ''
      input.setAttribute('aria-invalid', 'true')
    } else {
      input.removeAttribute('aria-invalid')
    }

    // If entering developer count for organization plan, deselect individual plans
    if (planType && CopilotCalculatorElement.usageCostInCents(planType) === null && value.trim() !== '') {
      for (const plan of this.plans) {
        plan.checked = false
      }
      for (const label of this.planLabels) {
        label.classList.remove('gradient-border')
      }
    }

    this.costPerMonthDisplay.setAttribute('aria-live', 'polite')
    this.calculateAndUpdateCost()
  }

  calculateAndUpdateCost() {
    let totalCost = 0

    // Handle individual plans when selected
    if (this.selectedPlan) {
      const planData = this.unit_data[this.plan]
      const isIndividualPlan =
        this.plan === 'copilot_pro' || this.plan === 'copilot_free' || this.plan === 'copilot_pro_plus'

      if (isIndividualPlan) {
        totalCost = planData?.value || 0
      }
    }

    // Always calculate AI credit costs and organization plans
    for (const input of this.developerCountInputs) {
      const planType = input.getAttribute('data-plan-type')
      const value = input.value.trim()

      if (!planType || !value) continue

      const count = parseInt(value)
      if (isNaN(count) || count < 1) continue

      const usageCostInCents = CopilotCalculatorElement.usageCostInCents(planType)
      if (usageCostInCents !== null) {
        totalCost += count * usageCostInCents
      } else {
        // Only add org plan costs if no individual plan is selected
        if (!this.selectedPlan) {
          const orgPlanData = this.unit_data[planType]
          if (!orgPlanData?.value) continue

          const planCost = count * orgPlanData.value
          totalCost += planCost
        }
      }
    }

    this.costPerMonth = totalCost
    const formattedCost = currencyFormatter.format(totalCost / 100)
    this.costPerMonthDisplay.textContent = formattedCost

    // Only dispatch events if section is active
    if (this.active) {
      document.dispatchEvent(
        new CustomEvent('subtotalChanged', {
          detail: {sectionName: 'copilotTotal', amount: totalCost, active: true},
        }),
      )

      document.dispatchEvent(
        new CustomEvent('formDataUpdated', {
          detail: {
            sectionName: 'copilot',
            resetEnabled: totalCost > 0,
          },
        }),
      )
    }
  }

  updateCost = this.calculateAndUpdateCost

  resetButtonClicked(event: PointerEvent) {
    event.preventDefault()
    for (const input of this.developerCountInputs) {
      input.value = ''
    }
    for (const plan of this.plans) {
      plan.checked = false
    }
    for (const planLabel of this.planLabels) {
      planLabel.classList.remove('gradient-border')
    }
    this.costPerMonth = 0
    this.updateCost()
  }

  get costPerDevPerMonth() {
    // Individual plans represent one developer, so include AI credit costs in the per-developer total.
    if (this.selectedPlan) {
      const isIndividualPlan =
        this.plan === 'copilot_pro' || this.plan === 'copilot_free' || this.plan === 'copilot_pro_plus'
      if (isIndividualPlan) {
        return this.getDeveloperCount(CopilotCalculatorElement.AI_CREDITS) > 0 ? this.costPerMonth : this.planPrice
      }
    }

    // For organization plans, calculate total cost divided by total developers
    let totalDevelopers = 0
    let totalCost = 0

    for (const input of this.developerCountInputs) {
      const planType = input.getAttribute('data-plan-type')
      const value = input.value.trim()
      if (!planType || !value) continue

      const count = parseInt(value)
      if (isNaN(count) || count < 1) continue

      const usageCostInCents = CopilotCalculatorElement.usageCostInCents(planType)
      if (usageCostInCents !== null) {
        totalCost += count * usageCostInCents
      } else {
        const orgPlanData = this.unit_data[planType]
        if (!orgPlanData?.value) continue

        totalDevelopers += count
        totalCost += count * orgPlanData.value
      }
    }

    return totalDevelopers > 0 ? totalCost / totalDevelopers : 0
  }

  getSummaryForCSV() {
    const details = this.developerCountInputs
      .map(input => {
        const planType = input.getAttribute('data-plan-type')
        const count = this.getDeveloperCount(planType || '')
        if (!count) return null

        if (planType === CopilotCalculatorElement.AI_CREDITS) {
          return `${CopilotCalculatorElement.AI_CREDITS}: ${count}`
        }
        if (planType === CopilotCalculatorElement.LEGACY_OVERAGE) {
          return `${CopilotCalculatorElement.LEGACY_OVERAGE_REQUESTS}: ${count}`
        }

        const planData = this.unit_data[planType || '']
        if (!planData) return null
        return `${planData.name || planType}: ${count} developers`
      })
      .filter(Boolean)
      .join(', ')

    return [
      [
        'GitHub Copilot',
        `"${details}"`,
        `"Cost Per Developer Per Month: ${currencyFormatter.format(this.costPerDevPerMonth / 100)}"`,
        `"${currencyFormatter.format(this.costPerMonth / 100)}"`,
      ],
    ]
  }

  static observedAttributes = ['data-active']
  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return

    if (name === 'data-active') {
      this.active = newValue === 'true' || newValue === ''

      if (!this.active) {
        document.dispatchEvent(
          new CustomEvent('subtotalChanged', {
            detail: {sectionName: 'copilotTotal', amount: 0, active: false},
          }),
        )
      } else {
        this.updateCost()
      }
    }
  }
}
